import React, { useState, useEffect, useCallback } from 'react';
import { getApiBase, fetchWithTimeout } from './apiBase';

/**
 * TrendsView — full-screen "what's hot right now" surface.
 * Hits GET /api/trends/topics?trade=<x>&region=US, renders blended topics
 * with source pills and a sample link. Lets the user switch trade filter
 * inline. Refresh button forces a re-fetch (server cache is 60min so a
 * click within the window returns cached results instantly).
 */

const TRADE_OPTIONS = [
  { id: 'general',      label: 'Everything' },
  { id: 'hvac',         label: 'HVAC' },
  { id: 'plumbing',     label: 'Plumbing' },
  { id: 'electrical',   label: 'Electrical' },
  { id: 'roofing',      label: 'Roofing' },
  { id: 'landscaping',  label: 'Landscaping' },
  { id: 'real_estate',  label: 'Real estate' },
  { id: 'construction', label: 'Construction' },
  { id: 'cleaning',     label: 'Cleaning' },
  { id: 'marketing',    label: 'Marketing' },
  { id: 'tech',         label: 'Tech / AI' },
  { id: 'smb',          label: 'Small biz' },
];

const SOURCE_PILL = {
  google:        { label: 'Google',     color: '#4285F4' },
  reddit:        { label: 'Reddit',     color: '#FF4500' },
  hackernews:    { label: 'HN',         color: '#FF6600' },
  tiktok:        { label: 'TikTok',     color: '#FE2C55' },
  tiktok_ads:    { label: 'TT Ads',     color: '#FE2C55' },
  tiktok_sounds: { label: 'TT Sounds',  color: '#FE2C55' },
  meta_ads:      { label: 'Meta Ads',   color: '#1877F2' },
  reddit_viral:  { label: 'Viral',      color: '#FF4500' },
};

// Category tabs — let user slice by what kind of signal they're looking for.
const CATEGORY_TABS = [
  { id: 'all',         label: 'All' },
  { id: 'topic',       label: 'Topics' },
  { id: 'ad',          label: 'Ads' },
  { id: 'sound',       label: 'Sounds' },
  { id: 'hashtag',     label: 'Hashtags' },
  { id: 'viral_video', label: 'Viral' },
];

function TrendsView() {
  // Default trade comes from localStorage so the user's pick sticks.
  const [trade, setTrade] = useState(() => {
    try { return localStorage.getItem('aria_trends_trade') || 'general'; }
    catch (_) { return 'general'; }
  });
  const [category, setCategory] = useState(() => {
    try { return localStorage.getItem('aria_trends_category') || 'all'; }
    catch (_) { return 'all'; }
  });
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [generatedAt, setGeneratedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      const url = `${base}/api/trends/topics?trade=${encodeURIComponent(trade)}&region=US&limit=40`;
      const res = await fetchWithTimeout(url, { timeout: 12000 });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed to load trends');
      setTopics(Array.isArray(data.topics) ? data.topics : []);
      setGeneratedAt(data.generated_at || new Date().toISOString());
    } catch (e) {
      setError(e.message || 'Network error');
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [trade, refreshKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try { localStorage.setItem('aria_trends_trade', trade); } catch (_) {}
  }, [trade]);

  useEffect(() => {
    try { localStorage.setItem('aria_trends_category', category); } catch (_) {}
  }, [category]);

  // Filtered view — apply the active category to the loaded topics.
  const visibleTopics = category === 'all'
    ? topics
    : topics.filter((t) => (t.category || 'topic') === category);

  const onClickTopic = (t) => {
    // Hand the topic to Aria as a follow-up question. Fires the same
    // prefill-and-go-home event the Explore tiles use.
    try {
      window.dispatchEvent(new CustomEvent('aria:prefillAndGoHome', {
        detail: { prompt: `What's the angle on "${t.topic}" for my business? ` }
      }));
    } catch (_) {}
    try { window.location.hash = ''; } catch (_) {}
  };

  return (
    <div className="trends-view">
      <header className="trends-header">
        <h1 className="trends-title">What's hot right now</h1>
        <p className="trends-tagline">
          Blended live signal from Google · Reddit · Hacker News · TikTok.
          Topics relevant to your trade rank highest.
        </p>
        <div className="trends-controls">
          <div className="trends-trades" role="tablist">
            {TRADE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={trade === opt.id}
                className={`trends-trade-pill ${trade === opt.id ? 'trends-trade-pill--active' : ''}`}
                onClick={() => setTrade(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="trends-refresh"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={loading}
            aria-label="Refresh trends"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Category tabs — Topics vs Ads vs Sounds vs Viral, etc */}
        <div className="trends-categories" role="tablist">
          {CATEGORY_TABS.map((c) => {
            const count = c.id === 'all'
              ? topics.length
              : topics.filter((t) => (t.category || 'topic') === c.id).length;
            return (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={category === c.id}
                className={`trends-cat-tab ${category === c.id ? 'trends-cat-tab--active' : ''}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label} <span className="trends-cat-tab__count">{count}</span>
              </button>
            );
          })}
        </div>
      </header>

      <div className="trends-body">
        {error && <div className="trends-error">{error}</div>}
        {!error && loading && topics.length === 0 && (
          <div className="trends-empty">Pulling signal from 4 sources…</div>
        )}
        {!error && !loading && topics.length === 0 && (
          <div className="trends-empty">No trends matched this filter — try Everything.</div>
        )}
        {!error && !loading && topics.length > 0 && visibleTopics.length === 0 && (
          <div className="trends-empty">No {category} items in this trade right now — try a different category or trade.</div>
        )}
        {visibleTopics.length > 0 && (
          <ol className="trends-list">
            {visibleTopics.map((t, i) => (
              <li key={i} className="trends-item">
                <span className="trends-rank">{i + 1}</span>
                <div className="trends-item__body">
                  <button
                    type="button"
                    className="trends-item__topic"
                    onClick={() => onClickTopic(t)}
                    title="Ask Aria about this"
                  >
                    {t.topic}
                  </button>
                  <div className="trends-item__meta">
                    {(t.sources || []).map((s) => {
                      const pill = SOURCE_PILL[s] || { label: s, color: '#888' };
                      return (
                        <span
                          key={s}
                          className="trends-source-pill"
                          style={{ borderColor: pill.color, color: pill.color }}
                        >
                          {pill.label}
                        </span>
                      );
                    })}
                    {t.sample_url && (
                      <a
                        href={t.sample_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="trends-item__link"
                      >
                        See source ↗
                      </a>
                    )}
                  </div>
                </div>
                <span className="trends-score">{Math.round(t.score)}</span>
              </li>
            ))}
          </ol>
        )}
        {generatedAt && !loading && (
          <p className="trends-footer">
            Last updated {new Date(generatedAt).toLocaleTimeString()} · cached for 60 min · click any topic to ask Aria
          </p>
        )}
      </div>
    </div>
  );
}

export default React.memo(TrendsView);
